import { AsyncAction, CommanderThunkAction, WithAccountId, WithScope } from '@mews-commander/types';
import { createAction, lt, showSuccessAlert } from '@mews/framework';
import { AccountNoteAddParameters, AccountType } from '@mews-commander/data-access';
import { createFetchAction, createKeyedUpdateObject, createUpdateValue, localizeText } from '@mews-commander/utils';
import { ACCOUNT_NOTE_UPDATE_FAILED } from '@mews-commander/legacy-entities/src/ducks/accountNotes';
import {
    addAccountNote as addAccountNoteApi,
    deleteAccountNote as deleteAccountNoteApi,
    updateAccountNote as updateAccountNoteApi,
    fetchAccountNotesByAccountId,
} from '@mews-commander/legacy-entities/src/actions/accountNotes';
import { TextKey } from '@mews-commander/enums';
import { changePaginationIds, createPagination } from '../pagination/actions';
import { createAddAction, createDeleteAction, createEditAction } from '../entityManagement';
import { ACCOUNT_NOTES_INIT } from './actionTypes';
import { getSelectedAccountNote, getSortedAccountNoteIdsByAccountId } from './selectors';
import { AccountNoteFormValues } from './components/schema';

const ACCOUNT_NOTES_PAGE_SIZE = 7;

const createAccountNotesPagination =
    (accountId: string, scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const ids = getSortedAccountNoteIdsByAccountId(getState(), { accountId });
        return dispatch(createPagination(ids, ACCOUNT_NOTES_PAGE_SIZE, scope));
    };

const changeAccountNotesList =
    (accountId: string, scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const ids = getSortedAccountNoteIdsByAccountId(getState(), { accountId });
        dispatch(changePaginationIds(ids, scope));
    };

const accountNoteAdder = createAddAction(addAccountNoteApi);
export const addAccountNote =
    (
        { content, classification }: AccountNoteFormValues,
        accountId: string,
        scope: string
    ): CommanderThunkAction<AsyncAction> =>
    (dispatch) => {
        const newNote: AccountNoteAddParameters = {
            content,
            accountId,
            data: {
                discriminator: AccountType.Customer,
                value: {
                    classifications: [classification],
                },
            },
        };

        return dispatch(accountNoteAdder({ ...newNote }, scope)).then(async () => {
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
            await dispatch(fetchAccountNotesByAccountId({ accountId, extent: { profiles: true } }));
            dispatch(changeAccountNotesList(accountId, scope));
        });
    };

const accountNoteEditor = createEditAction(updateAccountNoteApi);
export const editAccountNote =
    (values: AccountNoteFormValues, accountId: string, scope: string): CommanderThunkAction<AsyncAction> =>
    (dispatch, getState) => {
        const current = getSelectedAccountNote(getState(), { scope });

        if (!current) {
            dispatch(createAction(ACCOUNT_NOTE_UPDATE_FAILED));
            return;
        }

        const accountNoteUpdates = [
            {
                id: current.id,
                content: createUpdateValue(current.content, values.content),
                data: {
                    discriminator: AccountType.Customer,
                    value: {
                        classificationUpdates: createKeyedUpdateObject(
                            [values.classification],
                            [...current.data.value.classifications, values.classification]
                        ),
                    },
                },
            },
        ];

        return dispatch(accountNoteEditor({ accountNoteUpdates, accountId }, scope)).then(async () => {
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
            await dispatch(fetchAccountNotesByAccountId({ accountId, extent: { profiles: true } }));
            dispatch(changeAccountNotesList(accountId, scope));
        });
    };

const accountNoteDeleter = createDeleteAction(deleteAccountNoteApi);
export const deleteAccountNote =
    (accountId: string, scope: string): CommanderThunkAction<AsyncAction> =>
    (dispatch) =>
        dispatch(accountNoteDeleter({}, scope)).then(() => {
            showSuccessAlert({
                description: lt('DeleteSuccessMessage', { entity: localizeText(TextKey.Note) }),
            });
            dispatch(changeAccountNotesList(accountId, scope));
        });

export const initCustomerAccountNotes =
    ({ accountId, scope }: WithAccountId & WithScope): CommanderThunkAction<AsyncAction> =>
    async (dispatch) => {
        const promise = dispatch(
            fetchAccountNotesByAccountId({
                accountId,
                extent: {
                    profiles: true,
                },
            })
        ).then(() => dispatch(createAccountNotesPagination(accountId, scope)));
        return dispatch(createFetchAction(ACCOUNT_NOTES_INIT, promise, { scope }));
    };
